import axios from 'axios';
import {
  createContext,
  ReactChild,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import { MemberType } from '../../types/Types';
import { getError } from '../../utils/getError.utils';
import ActionTypes from '../Action.types';

import MemberReducer from './Member.reducer';

interface MemerProviderProps {
  children: ReactChild;
}

const MemberContext = createContext({
  memberState: {
    members: [] as MemberType[],
    isLoading: false,
  },
  getAllMembers: () => {},
  createMember: (member: MemberType) => {},
  editMember: (member: MemberType) => {},
  deleteMember: (memberId: string) => {},
  createModal: false,
  setCreateModal: (show: boolean) => {},
  editModal: false,
  setEditModal: (show: boolean) => {},
  deleteModal: false,
  setDeleteModal: (show: boolean) => {},
});

const INITIAL_STATE = {
  members: [] as MemberType[],
  isLoading: false,
};
const MemberProvider = ({ children }: MemerProviderProps) => {
  const [state, dispatch] = useReducer(MemberReducer, INITIAL_STATE);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAllMembers = useCallback(async () => {
    dispatch({ type: ActionTypes.start, payload: '' });
    try {
      const res = await axios.get(`${backendUrl}/members`);
      dispatch({ type: ActionTypes.find, payload: res.data });
    } catch (error) {
      toast.error(`${getError(error)}`);
      dispatch({ type: ActionTypes.clear, payload: '' });
    }
  }, [backendUrl]);

  useEffect(() => {
    getAllMembers();
  }, [getAllMembers]);

  const createMember = async (member: MemberType) => {
    dispatch({ type: ActionTypes.start, payload: '' });
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');

    try {
      const res = await axios.post(`${backendUrl}/members`, member, {
        headers: { authorization: 'Bearer ' + userData.accessToken },
      });
      dispatch({ type: ActionTypes.create, payload: res.data });
      setCreateModal(false);
      toast.success('Successfully, Member has ben created');
    } catch (error) {
      toast.error(`${getError(error)}`);
      dispatch({ type: ActionTypes.clear, payload: '' });
    }
  };

  const editMember = async (member: MemberType) => {
    dispatch({ type: ActionTypes.start, payload: '' });
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    try {
      const res = await axios.put(
        `${backendUrl}/members/${member._id}`,
        member,
        {
          headers: { authorization: 'Bearer ' + userData.accessToken },
        }
      );
      dispatch({ type: ActionTypes.update, payload: res.data });
      setEditModal(false);
      toast.success('Successfully, Member has ben Updated');
    } catch (error) {
      toast.error(`${getError(error)}`);
      dispatch({ type: ActionTypes.clear, payload: '' });
    }
  };

  const deleteMember = async (memberId: string) => {
    dispatch({ type: ActionTypes.start, payload: '' });
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');

    try {
      const res = await axios.delete(`${backendUrl}/members/${memberId}`, {
        headers: { authorization: 'Bearer ' + userData.accessToken },
      });
      dispatch({ type: ActionTypes.delete, payload: res.data });
      setDeleteModal(false);
      toast.success('Successfully, Member has ben Deleted');
    } catch (error) {
      toast.error(`${getError(error)}`);
      dispatch({ type: ActionTypes.clear, payload: '' });
    }
  };

  return (
    <MemberContext.Provider
      value={{
        memberState: state,
        getAllMembers,
        createMember,
        editMember,
        deleteMember,
        createModal,
        setCreateModal,
        editModal,
        setEditModal,
        deleteModal,
        setDeleteModal,
      }}
    >
      {children}
    </MemberContext.Provider>
  );
};

export const MemberState = () => useContext(MemberContext);

export default MemberProvider;
